import { useEffect, useMemo, useRef, useState } from "react";
import * as d3 from "d3";

// --- TIPOS ---
type AxisLeftProps = {
  yScale: d3.ScaleLinear<number, number>;
  pixelsPerTick: number;
};

type AxisBottomProps = {
  xScale: d3.ScaleBand<string>;
};

type VerticalBoxProps = {
  min: number;
  q1: number;
  median: number;
  q3: number;
  max: number;
  width: number;
  stroke: string;
  fill: string;
};

type DataItem = {
  name: string;
  value: number;
};

type BoxProps = {
  data: DataItem[];
  title: string;
  xlim: string;
};

// --- FUNCIÓN AUXILIAR: ABBREVIATE LABEL ---
const abbreviateLabel = (label: string, maxLength = 15): string => {
  if (label.length <= maxLength) return label;
  let slice = label.slice(0, maxLength);
  slice = slice + "...";
  return slice;
};

// --- FUNCIÓN AUXILIAR: GET SUMMARY STATS ---
const getSummaryStats = (data2: number[]) => {
  if (data2.length === 0) return null;

  const sortedData = [...data2].sort((a, b) => a - b);

  const q1 = d3.quantile(sortedData, 0.25) as number;
  const median = d3.quantile(sortedData, 0.5) as number;
  const q3 = d3.quantile(sortedData, 0.75) as number;
  const interQuantileRange = q3 - q1;

  const minFence = q1 - 1.5 * interQuantileRange;
  const maxFence = q3 + 1.5 * interQuantileRange;

  const min = d3.min(sortedData.filter(d => d >= minFence)) ?? q1;
  const max = d3.max(sortedData.filter(d => d <= maxFence)) ?? q3;

  return { min, q1, median, q3, max };
};

// --- SUB-COMPONENTE: VERTICAL BOX ---
const VerticalBox = ({
  min,
  q1,
  median,
  q3,
  max,
  width,
  stroke,
  fill,
}: VerticalBoxProps) => (
  <>
    {/* Bigotes (líneas verticales) */}
    <line x1={width / 2} x2={width / 2} y1={max} y2={q3} stroke={stroke} />
    <line x1={width / 2} x2={width / 2} y1={q1} y2={min} stroke={stroke} />

    {/* Caja (rectángulo) */}
    <rect x={0} y={q3} width={width} height={q1 - q3} stroke={stroke} fill={fill} />

    {/* Línea de mediana */}
    <line x1={0} x2={width} y1={median} y2={median} stroke={stroke} />

    {/* Extremos de los bigotes (caps) */}
    <line x1={0} x2={width} y1={max} y2={max} stroke={stroke} />
    <line x1={0} x2={width} y1={min} y2={min} stroke={stroke} />
  </>
);

// --- SUB-COMPONENTE: AXIS LEFT ---
const AxisLeft = ({ yScale, pixelsPerTick }: AxisLeftProps) => {
  const range = yScale.range();
  const ticks = useMemo(() => {
    const height = range[0] - range[1];
    const numberOfTicksTarget = Math.floor(height / pixelsPerTick);
    return yScale.ticks(numberOfTicksTarget).map(value => ({
      value,
      yOffset: yScale(value),
    }));
  }, [yScale]);

  return (
    <>
      <path d={["M", 0, range[0], "L", 0, range[1]].join(" ")} fill="none" stroke="currentColor" />
      {ticks.map(({ value, yOffset }) => (
        <g key={value} transform={`translate(0, ${yOffset})`}>
          <line x2={-6} stroke="currentColor" />
          <text style={{ fontSize: "10px", textAnchor: "middle", transform: "translateX(-20px)" }}>
            {value}
          </text>
        </g>
      ))}
    </>
  );
};

// --- SUB-COMPONENTE: AXIS BOTTOM (MODIFICADO) ---
const AxisBottom = ({ xScale }: AxisBottomProps) => {
  const [min, max] = xScale.range();
  const ticks = useMemo(() => xScale.domain().map(value => ({
    value,
    xOffset: xScale(value)! + xScale.bandwidth() / 2,
  })), [xScale]);

  return (
    <>
      <path d={["M", min, 0, "L", max, 0].join(" ")} fill="none" stroke="currentColor" />
      {ticks.map(({ value, xOffset }) => (
        <g key={value} transform={`translate(${xOffset}, 0)`}>
          <line y2={6} stroke="currentColor" />
          {/* El elemento <text> que contenía las etiquetas se ha eliminado */}
        </g>
      ))}
    </>
  );
};

// --- COMPONENTE PRINCIPAL: BOXPLOT ---

const BoxPlot = ({ data, title, xlim }: BoxProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const resizeTimeout = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const resizeObserver = new ResizeObserver(entries => {
      if (entries[0].contentRect) {
        if (resizeTimeout.current) {
          clearTimeout(resizeTimeout.current);
        }
        resizeTimeout.current = setTimeout(() => {
          setDimensions({
            width: entries[0].contentRect.width,
            height: entries[0].contentRect.height
          });
        }, 10);
      }
    });

    if (ref.current) {
      resizeObserver.observe(ref.current);
    }

    return () => {
      resizeObserver.disconnect();
      if (resizeTimeout.current) {
        clearTimeout(resizeTimeout.current);
      }
    };
  }, []);

  const MARGIN = { top: 50, right: 50, bottom: 50, left: 70 };
  const boundsWidth = dimensions.width - MARGIN.right - MARGIN.left;
  const boundsHeight = dimensions.height - MARGIN.top - MARGIN.bottom;

  // MEMO 1: Calcular estadísticas y grupos
  const { groups, allStats } = useMemo(() => {
    const groups = Array.from(new Set(data.map(d => d.name)));
    const allStats = groups.map(group => {
        const groupData = data.filter(d => d.name === group).map(d => d.value);
        return { group, stats: getSummaryStats(groupData) };
      })
      .filter(d => d.stats !== null); // Filtramos grupos sin datos
      
    return { 
      groups: allStats.map(d => d.group),
      allStats: allStats.map(d => d.stats) as { min: number, q1: number, median: number, q3: number, max: number }[]
    };
  }, [data]);

  // MEMO 2: Calcular dominio Y (con padding)
  const { dataMinNum, dataMaxNum } = useMemo(() => {
    const [minStr, maxStr] = xlim?.split(',') || [];
    let dataMinNum = parseFloat(minStr); // ¡Usar parseFloat!
    let dataMaxNum = parseFloat(maxStr);

    // Fallback si xlim no es válido
    if (isNaN(dataMinNum) || isNaN(dataMaxNum)) {
      // Basar el dominio en las estadísticas (bigotes), no en los datos brutos
      const min = d3.min(allStats.map(s => s.min)) ?? 0;
      const max = d3.max(allStats.map(s => s.max)) ?? 100;
      
      if (isNaN(dataMinNum)) dataMinNum = min;
      if (isNaN(dataMaxNum)) dataMaxNum = max;
    }

    // Añadir padding para que los bigotes no toquen los bordes
    const range = dataMaxNum - dataMinNum;
    let padding;

    if (range === 0) {
      // Si min y max son iguales, añade un padding fijo
      padding = dataMaxNum * 0.1 > 0 ? dataMaxNum * 0.1 : 1;
    } else {
      // Añade un 10% de padding (5% arriba, 5% abajo)
      padding = range * 0.1;
    }
    
    return {
       dataMinNum: dataMinNum - padding,
       dataMaxNum: dataMaxNum + padding,
    };
  }, [xlim, allStats]);

  // MEMO 3: Escalas Y y X
  const yScale = useMemo(() => 
    d3.scaleLinear()
      .domain([dataMinNum, dataMaxNum])
      .range([boundsHeight, 0])
      .nice(), // .nice() ajusta el dominio a números "bonitos"
    [dataMinNum, dataMaxNum, boundsHeight]);

  const xScale = useMemo(() => 
    d3.scaleBand()
      .domain(groups) // 'groups' viene del MEMO 1
      .range([0, boundsWidth])
      .padding(0.25), 
    [groups, boundsWidth]);

  // MEMO 4: Escala de Color
  const colorScale = useMemo(() => {
    return d3.scaleOrdinal<string>()
      .domain(groups)
      .range(d3.schemeCategory10); // Paleta de 10 colores de D3
  }, [groups]);


  // MEMO 5: Renderizar las formas (cajas)
  const allShapes = useMemo(() => allStats.map((sumStats, i) => {
    const group = groups[i]; // Corresponder stats con grupo
    if (!sumStats) return null;
    
    const { min, q1, median, q3, max } = sumStats;
    
    return (
      <g key={i} transform={`translate(${xScale(group)},0)`}>
        <VerticalBox
          width={xScale.bandwidth()}
          q1={yScale(q1)}
          median={yScale(median)}
          q3={yScale(q3)}
          min={yScale(min)}
          max={yScale(max)}
          stroke="black"
          fill={colorScale(group)} // Aplicar color
        />
      </g>
    );
  }), [allStats, groups, xScale, yScale, colorScale]);

  // --- RENDERIZADO DEL COMPONENTE ---
  return (
    <div style={{ display: 'flex', width: '100%', height: '100%' }}>
      
      {/* Contenedor del Gráfico (flexible) */}
      <div ref={ref} style={{ flex: 1, height: '100%', position: 'relative' }}>
        <svg width={dimensions.width} height={dimensions.height} style={{ position: 'absolute' }}>
          <text
            x={dimensions.width / 2}
            y={MARGIN.top / 2}
            textAnchor="middle"
            style={{ fontSize: '16px', fontWeight: 'bold' }}
          >
            {title}
          </text>
          <g transform={`translate(${MARGIN.left},${MARGIN.top})`}>
            {allShapes}
            <AxisLeft yScale={yScale} pixelsPerTick={30} />
            <g transform={`translate(0, ${boundsHeight})`}>
              <AxisBottom xScale={xScale} />
            </g>
          </g>
        </svg>
      </div>

      {/* Contenedor de la Leyenda */}
      <div style={{
        width: '200px', // Ancho fijo para la leyenda
        height: '100%',
        overflowY: 'auto',
        padding: '10px',
        boxSizing: 'border-box',
        borderLeft: '1px solid #ccc'
      }}>
        <h4 style={{ marginTop: 0, marginBottom: '10px' }}>Leyenda</h4>
        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {groups.map((group) => (
            <li key={group} style={{ display: 'flex', alignItems: 'center', marginBottom: '5px', fontSize: '12px' }}>
              <span style={{
                width: '12px',
                height: '12px',
                backgroundColor: colorScale(group),
                border: '1px solid #555',
                marginRight: '8px',
                flexShrink: 0
              }} />
              <span>{group}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default BoxPlot;